/**
 * Hook: useUserActions
 *
 * Manages state for user actions bottom sheet.
 * Matches Flutter implementation: lib/presentation/bottom_sheet/user_actions_bottom_sheet.dart
 *
 * Responsibilities:
 * - Fetches user if only userId is provided
 * - Determines if viewing current user or peer
 * - Checks if user is saved to favorites
 * - Checks if user is blocked
 * - Provides reactive updates when storage changes
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserActionEntity } from '@/domain/entities/userAction/userActionEntity';
import { UserActionType } from '@/domain/enums/userAction/userActionType';
import { createFetchUserDetailsUseCase } from '@/domain/usecases/user/fetchUserDetailsUseCase';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { useListenToSecureStorageUpdates } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { SavedUserStorage, BlockedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { saveUserToFavoritesUseCase } from '@/domain/usecases/user/saveUserToFavoritesUseCase';
import { blockUserUseCase } from '@/domain/usecases/user/blockUserUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export interface UseUserActionsProps {
  user?: UserEntity;
  userId?: string;
  /**
   * When `true`, the current-user action list also includes the owner
   * account-management items (Edit profile, Settings, Help & About, Log out,
   * Delete account). Passed as `true` only from the `UserDetailsPage`
   * top-app-bar entry; left `false` (default) from the `ContentListItem` entry.
   */
  includeAccountActions?: boolean;
}

export interface UseUserActionsResult {
  user: UserEntity | null;
  isCurrentUser: boolean;
  userIsSaved: boolean;
  userIsBlocked: boolean;
  loading: boolean;
  error: Error | null;
  saveToFavorites: () => Promise<boolean>;
  blockUser: () => Promise<boolean>;
  userActions: UserActionEntity[];
}

export function useUserActions({
  user: initialUser,
  userId,
  includeAccountActions = false,
}: UseUserActionsProps): UseUserActionsResult {
  const { l10n } = useLocale();
  const [user, setUser] = useState<UserEntity | null>(initialUser || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Get current authenticated user
  const currentFirebaseUser = getAuthFirebaseUserUseCase.execute();
  const currentUserId = currentFirebaseUser?.uid;

  // Determine user ID to check
  const userIdToCheck = user?.firebaseId || userId;

  // Check if it's the current user
  const isCurrentUser = useMemo(() => {
    if (!currentUserId || !userIdToCheck) return false;
    return currentUserId === userIdToCheck;
  }, [currentUserId, userIdToCheck]);

  // Reactive storage checks
  const savedStorageKey = userIdToCheck ? SECURE_STORAGE_KEYS.SAVED_USERS + userIdToCheck : '';
  const blockedStorageKey = userIdToCheck ? SECURE_STORAGE_KEYS.BLOCKED_USERS + userIdToCheck : '';

  const { data: savedUserData } = useListenToSecureStorageUpdates<SavedUserStorage>(savedStorageKey);
  const { data: blockedUserData } = useListenToSecureStorageUpdates<BlockedUserStorage>(blockedStorageKey);

  const userIsSaved = savedUserData !== null;
  const userIsBlocked = blockedUserData !== null;

  // Fetch user if only userId provided
  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
      return;
    }

    if (!userId) {
      setError(new Error('Either user or userId must be provided'));
      return;
    }

    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchUserDetailsUseCase = createFetchUserDetailsUseCase();
        const fetchedUser = await fetchUserDetailsUseCase.execute(userId);
        if (!fetchedUser) {
          setError(new Error(`User not found: ${userId}`));
          return;
        }
        setUser(fetchedUser);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch user'));
        appLogger.error('Error fetching user', 'useUserActions.fetchUser', { error: err as Error });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [initialUser, userId]);

  const saveToFavorites = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    return saveUserToFavoritesUseCase.execute(user.firebaseId);
  }, [user]);

  const blockUser = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    return blockUserUseCase.execute(user.firebaseId);
  }, [user]);

  /**
   * Builds the action list for the current (signed-in) user.
   *
   * Flutter parity: lib/presentation/bottom_sheet/user_actions_bottom_sheet.dart
   * getCurrentUserActions() (:150-187) — blocked current user (`_user.blocked`,
   * :153-162) yields only "Appeal blocked account"; otherwise the base items are
   * Share profile with friends (:164-170) and Share profile outside Expause
   * (:171-177), followed by Edit profile (:178-184).
   *
   * Directed web-vs-Flutter divergence (task-prompt authorised — parity reviewer
   * must NOT flag): the owner account-management items — Edit profile, Settings,
   * Help & About, Log out, Delete account — live on this sheet only on web
   * (Flutter's `UserActionType` enum has no such values; they live elsewhere in
   * Flutter). Per the task prompt these five items (including Edit profile) are
   * appended only when the sheet is opened from the `UserDetailsPage` app-bar
   * entry (`includeAccountActions === true`); from the `ContentListItem` entry
   * the current user sees only Share + Share-Social. This intentionally differs
   * from Flutter, which always includes Edit for the current user — Flutter is
   * left as-is.
   */
  const getCurrentUserActions = (): UserActionEntity[] => {
    if (!user) return [];
    const result: UserActionEntity[] = [];

    // If current user is blocked, only show appeal option
    if (user.blocked) {
      result.push({
        image: '/assets/images/appeal.png',
        name: l10n.userActions.appealBlockedAccount,
        type: UserActionType.APPEAL,
      });
      return result;
    }

    result.push({
      image: '/assets/images/share.png',
      name: l10n.userActions.shareProfileWithFriends,
      type: UserActionType.SHARE,
    });
    result.push({
      image: '/assets/images/share_social.png',
      name: l10n.userActions.shareProfileOutsideExpause,
      type: UserActionType.SHARE_SOCIAL,
    });

    // Owner account-management items — web-only, gated to the UserDetailsPage entry.
    if (includeAccountActions) {
      result.push({
        image: '/assets/images/bio.png',
        name: l10n.common.editProfile,
        type: UserActionType.EDIT,
      });
      result.push({
        image: '/assets/images/settings.png',
        name: l10n.userActions.settings,
        type: UserActionType.SETTINGS,
      });
      result.push({
        image: '/assets/images/info.png',
        name: l10n.userActions.helpAbout,
        type: UserActionType.HELP_ABOUT,
      });
      result.push({
        image: '/assets/images/log_out.png',
        name: l10n.userActions.logout,
        type: UserActionType.LOGOUT,
      });
      result.push({
        image: '/assets/images/delete_account.png',
        name: l10n.userActions.deleteAccount,
        type: UserActionType.DELETE_ACCOUNT,
      });
    }

    return result;
  };

  /**
   * Builds the action list for a peer (other) user.
   *
   * Flutter parity: lib/presentation/bottom_sheet/user_actions_bottom_sheet.dart
   * getPeerUserActions() (:189-232) — Share (:191-197), Share-Social (:198-204),
   * Save/Remove favourite (:205-214), Report (:215-221), Block/Un-block +
   * firstName (:223-229). The commented-out `copy` item (:222) is dead Flutter
   * code and intentionally not ported.
   */
  const getPeerUserActions = (): UserActionEntity[] => {
    if (!user) return [];
    const result: UserActionEntity[] = [];

    result.push({
      image: '/assets/images/share.png',
      name: l10n.userActions.shareProfileWithFriends,
      type: UserActionType.SHARE,
    });
    result.push({
      image: '/assets/images/share_social.png',
      name: l10n.userActions.shareProfileOutsideExpause,
      type: UserActionType.SHARE_SOCIAL,
    });
    result.push({
      image: '/assets/images/star.png',
      name: userIsSaved
        ? l10n.userActions.removeFromFavouritePeople
        : l10n.userActions.saveToFavouritePeople,
      type: UserActionType.SAVE,
    });
    result.push({
      image: '/assets/images/report.png',
      name: l10n.userActions.reportUser,
      type: UserActionType.REPORT,
    });
    result.push({
      image: '/assets/images/block_user.png',
      name: (userIsBlocked ? l10n.userActions.unblockUser : l10n.userActions.blockUser) + ' ' + user.firstName,
      type: UserActionType.BLOCK,
    });

    return result;
  };

  // Selection mirrors Flutter's `_isCurrentUser ? getCurrentUserActions() : getPeerUserActions()`
  // (user_actions_bottom_sheet.dart:89-90).
  const userActions = useMemo(
    () => (isCurrentUser ? getCurrentUserActions() : getPeerUserActions()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCurrentUser, userIsSaved, userIsBlocked, user, l10n, includeAccountActions],
  );

  return {
    user,
    isCurrentUser,
    userIsSaved,
    userIsBlocked,
    loading,
    error,
    saveToFavorites,
    blockUser,
    userActions,
  };
}
